import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { apiRequest } from '../lib/api-client';
import { formatDateTime, formatRelative } from '../ui/format';
import { Badge, type BadgeTone } from '../ui/Page';
import { useConnection } from './ConnectionStatus';
import { ReportPanel } from './ReportPanel';
import { useRealtimeReconnect, useRealtimeTopic } from './realtime-store';

/**
 * The status bell: what Safe Cortex has to say, and how the platform is doing.
 *
 * Next to the notification bell but not the same thing — notifications are about this tenant's
 * work; this is about the product. A release note, a notice, an incident under way; and the
 * platform's own dependency checks with the response time this tab measured, so "is it me or is
 * it them" is one click away.
 */

export type AnnouncementKind = 'RELEASE' | 'NOTICE' | 'INCIDENT' | 'MAINTENANCE';

interface Announcement {
  id: string;
  kind: AnnouncementKind;
  title: string;
  body: string;
  version: string | null;
  publishedAt: string | null;
  resolvedAt: string | null;
  read: boolean;
}

interface Status {
  status: 'OPERATIONAL' | 'DEGRADED' | 'INCIDENT' | 'MAINTENANCE';
  /** The release running on the server — the tag the deploy checked out. */
  version: string;
  checkedAt: string;
  dependencies: { name: string; status: string; durationMs: number }[];
  incident: Announcement | null;
}

const kindTone: Record<AnnouncementKind, BadgeTone> = {
  RELEASE: 'info',
  NOTICE: 'neutral',
  INCIDENT: 'danger',
  MAINTENANCE: 'warning',
};

export function StatusBell(): React.JSX.Element {
  const client = useQueryClient();
  const panel = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const connection = useConnection();

  const feed = useQuery({
    queryKey: ['announcements'],
    queryFn: () =>
      apiRequest<{ announcements: Announcement[]; unread: number }>('/v1/announcements'),
    // A floor under the socket, not the way this learns things.
    refetchInterval: 15 * 60_000,
  });

  const status = useQuery({
    queryKey: ['platform-status'],
    queryFn: () => apiRequest<Status>('/v1/status'),
    refetchInterval: 60_000,
  });

  /*
   * An incident arrives when it happens, not when somebody reloads (Phase 98).
   *
   * The frame carries no content — each session re-reads the feed, where its own read marks are
   * applied — so what arrives here is only the news that there is news. The polls stay as a floor:
   * a socket that is silently down looks exactly like a quiet afternoon, and this is the one bell
   * where being quietly wrong means not knowing the product is down.
   */
  useRealtimeTopic('platform', () => {
    void client.invalidateQueries({ queryKey: ['announcements'] });
    void client.invalidateQueries({ queryKey: ['platform-status'] });
  });

  useRealtimeReconnect(() => {
    void client.invalidateQueries({ queryKey: ['announcements'] });
    void client.invalidateQueries({ queryKey: ['platform-status'] });
  });

  const readAll = useMutation({
    mutationFn: () => apiRequest('/v1/announcements/read-all', { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['announcements'] }),
  });

  const readOne = useMutation({
    mutationFn: (id: string) => apiRequest(`/v1/announcements/${id}/read`, { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['announcements'] }),
  });

  useEffect(() => {
    if (!open) return;

    const onClick = (event: MouseEvent): void => {
      if (!panel.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = feed.data?.unread ?? 0;
  const overall: Status['status'] =
    connection.status === 'OFFLINE' ? 'DEGRADED' : (status.data?.status ?? 'OPERATIONAL');
  const tone =
    overall === 'OPERATIONAL'
      ? 'ok'
      : overall === 'DEGRADED' || overall === 'MAINTENANCE'
        ? 'warn'
        : 'bad';

  return (
    <div className="bell" ref={panel}>
      <button
        type="button"
        className={`topbar__action statusbell statusbell--${tone}`}
        aria-label={t('status.bellTitle')}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M12 2 2 20h20L12 2z" />
          <path d="M12 9v5" />
          <circle cx="12" cy="17" r="0.8" fill="currentColor" />
        </svg>
        {unread > 0 && <span className="bell__count">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="bell__panel statusbell__panel">
          <header className="bell__head">
            <strong>{t('status.bellTitle')}</strong>
            {unread > 0 && (
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => readAll.mutate()}
              >
                {t('status.readAll')}
              </button>
            )}
          </header>

          {/*
            * An incident, and only an incident (Phase 129).
            *
            * The dependency check that used to live here — "postgres ✓ redis ✓ rabbitmq ✓" — is an
            * operator's answer to an operator's question, and the person running a bakery does not
            * have that question. What stays is the half that is about them: when something *is*
            * wrong, saying so here is what turns "the product is broken" into "they know", and
            * hiding it would only move the discovery into a support ticket.
            */}
          {status.data?.incident && (
            <div className={`statusbell__status statusbell__status--${tone}`}>
              <span className="statusbell__dot" aria-hidden="true" />
              <span className="cell-stack">
                <span className="strong">{t(`status.${overall}`)}</span>
                <span className="small">{status.data.incident.title}</span>
              </span>
            </div>
          )}

          <ReportPanel />

          {/*
            * Which version is running (Phase 129).
            *
            * Read from the server rather than baked into this bundle, because the bundle is not
            * what people ask about: "está na versão nova?" is a question about the machine, and a
            * cached tab would answer it with whatever it was built from.
            */}
          <p className="statusbell__version muted small">
            {t('status.version').replace('{version}', status.data?.version ?? '—')}
          </p>

          {(feed.data?.announcements.length ?? 0) === 0 ? (
            <p className="muted small bell__empty">{t('status.nothingNew')}</p>
          ) : (
            <ul className="bell__list">
              {feed.data!.announcements.map((item) => (
                <li key={item.id} className={item.read ? '' : 'bell__item--unread'}>
                  <button
                    type="button"
                    className="bell__item statusbell__item"
                    onClick={() => !item.read && readOne.mutate(item.id)}
                  >
                    <span className="cell-inline">
                      <Badge tone={item.resolvedAt ? 'neutral' : kindTone[item.kind]}>
                        {t(`status.kind.${item.kind}`)}
                        {item.version ? ` ${item.version}` : ''}
                      </Badge>
                      <span className="strong">{item.title}</span>
                    </span>
                    <span className="statusbell__body">{item.body}</span>
                    <span
                      className="muted small"
                      title={item.publishedAt ? formatDateTime(item.publishedAt) : ''}
                    >
                      {item.publishedAt ? formatRelative(item.publishedAt) : ''}
                      {item.resolvedAt ? ` · ${t('status.resolved')}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
